import React, { useState, useEffect } from 'react';
import { X, BarChart3, TrendingUp, Clock, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';

const AnalyticsModal = ({ onClose, workflowId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(false);

  // Sample analytics data
  const sampleAnalytics = {
    executions: {
      total: 245,
      successful: 221,
      failed: 24,
      successRate: 90.2
    },
    performance: {
      avgExecutionTime: 3.4,
      minExecutionTime: 1.2,
      maxExecutionTime: 12.8,
      totalRuntime: 833
    },
    trends: {
      daily: [
        { date: '2024-01-01', executions: 15, avgTime: 3.2, successRate: 93 },
        { date: '2024-01-02', executions: 22, avgTime: 2.8, successRate: 95 },
        { date: '2024-01-03', executions: 18, avgTime: 4.1, successRate: 89 },
        { date: '2024-01-04', executions: 31, avgTime: 3.0, successRate: 94 },
        { date: '2024-01-05', executions: 28, avgTime: 3.8, successRate: 86 },
        { date: '2024-01-06', executions: 35, avgTime: 2.9, successRate: 97 },
        { date: '2024-01-07', executions: 41, avgTime: 3.1, successRate: 92 },
      ]
    },
    nodePerformance: [
      { nodeId: 'ai-1', nodeType: 'AI', avgTime: 2.1, errorRate: 5.2 },
      { nodeId: 'db-1', nodeType: 'Database', avgTime: 0.8, errorRate: 1.1 },
      { nodeId: 'api-1', nodeType: 'API', avgTime: 1.5, errorRate: 8.3 },
      { nodeId: 'file-1', nodeType: 'File', avgTime: 3.2, errorRate: 2.8 },
    ],
    errors: [
      { timestamp: '2024-01-07T14:30:00Z', nodeId: 'api-1', error: 'Connection timeout', count: 3 },
      { timestamp: '2024-01-07T11:15:00Z', nodeId: 'ai-1', error: 'Rate limit exceeded', count: 2 },
      { timestamp: '2024-01-06T16:45:00Z', nodeId: 'file-1', error: 'File not found', count: 1 },
    ]
  };

  useEffect(() => {
    setAnalytics(sampleAnalytics);
  }, [workflowId, timeRange]);

  const StatCard = ({ title, value, subtext, icon: Icon, color = "text-blue-600" }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtext && <p className="text-xs text-gray-500">{subtext}</p>}
        </div>
        <Icon className={`h-8 w-8 ${color}`} />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-50 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-white p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Workflow Analytics</h2>
              <p className="text-gray-600">Performance metrics and execution analytics</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {analytics ? (
            <div className="space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard
                  title="Total Executions"
                  value={analytics.executions.total}
                  icon={BarChart3}
                  color="text-blue-600"
                />
                <StatCard
                  title="Success Rate"
                  value={`${analytics.executions.successRate}%`}
                  subtext={`${analytics.executions.successful}/${analytics.executions.total}`}
                  icon={CheckCircle}
                  color="text-green-600"
                />
                <StatCard
                  title="Avg Execution Time"
                  value={`${analytics.performance.avgExecutionTime}s`}
                  subtext={`Min: ${analytics.performance.minExecutionTime}s, Max: ${analytics.performance.maxExecutionTime}s`}
                  icon={Clock}
                  color="text-yellow-600"
                />
                <StatCard
                  title="Failed Executions"
                  value={analytics.executions.failed}
                  subtext={`${(100 - analytics.executions.successRate).toFixed(1)}% failure rate`}
                  icon={AlertTriangle}
                  color="text-red-600"
                />
              </div>

              {/* Execution Trends */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Execution Trends</h3>
                <div className="space-y-4">
                  {analytics.trends.daily.map((day, index) => (
                    <div key={day.date} className="flex items-center space-x-4">
                      <div className="w-24 text-sm text-gray-600">
                        {new Date(day.date).toLocaleDateString()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{day.executions} executions</span>
                          <span className="text-sm text-gray-600">{day.avgTime}s avg</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${(day.executions / 50) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        day.successRate >= 95 ? 'bg-green-100 text-green-800' :
                        day.successRate >= 90 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {day.successRate}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Node Performance */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Node Performance</h3>
                  <div className="space-y-3">
                    {analytics.nodePerformance.map((node) => (
                      <div key={node.nodeId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">{node.nodeType}</div>
                          <div className="text-sm text-gray-600">{node.nodeId}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{node.avgTime}s avg</div>
                          <div className={`text-xs ${
                            node.errorRate < 5 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {node.errorRate}% errors
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Errors */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Errors</h3>
                  <div className="space-y-3">
                    {analytics.errors.map((error, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{error.error}</div>
                          <div className="text-sm text-gray-600">Node: {error.nodeId}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(error.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded">
                          {error.count}x
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Resource Usage */}
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{analytics.performance.totalRuntime}s</div>
                    <div className="text-sm text-gray-600">Total Runtime</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">24</div>
                    <div className="text-sm text-gray-600">AI API Calls</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">156</div>
                    <div className="text-sm text-gray-600">DB Operations</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-500">Loading analytics...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModal;